import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import { colors } from 'bl-common/src/constants/colors'
import { durations } from 'bl-common/src/constants/durations'
import { gridOffsetFrom } from 'bl-common/src/constants/sizes'
import { Link } from 'bl-common/src/elements/Link'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { ResponsiveParallax } from 'bl-common/src/units/ResponsiveParallax'
import { StyledImage } from 'bl-common/src/units/SmartImage/StyledImage'
import { media, mediaMax } from 'bl-common/src/utils/media'
import { formatDateInUTC } from 'bl-utils/src/formatting/formatDate'

const Container = styled.div`
  align-self: flex-start;
  display: block;
  flex: 1;
  position: relative;

  ${mediaMax.md(css`
    margin-bottom: 36px;
    margin-right: ${-gridOffsetFrom}px;
  `)}
`

const YOFFSET = 80

const Content = styled.div<{ hasDecorator?: boolean }>`
  flex: 1;
  margin-top: -${YOFFSET}px;
  padding-right: ${3 * gridOffsetFrom}px;

  ${({ hasDecorator }) =>
    hasDecorator &&
    css`
      width: calc(100% - 100px);
    `}

  ${media.md(css`
    margin-left: 0;
    padding-left: 0;
    padding-right: ${2 * gridOffsetFrom}px;
  `)}
`

const ImageWrap = styled.div`
  position: relative;
  top: -80px;
  overflow: hidden;
  width: calc(100% - 64px);
  ${media.md(css`
    width: calc(100% - 100px);
  `)}
`

const Image = styled(StyledImage)<{ ratio: number }>`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: 1;

  &::before {
    content: '';
    display: block;
    padding-bottom: ${({ ratio }) => ratio * 100}%;
  }

  transform: scale(1);
  opacity: 0.9;
  transition:
    opacity ${durations.short}ms,
    transform ${durations.long}ms;
`

// OuterLink is the main hover trigger
const OuterLink = styled(({ ...rest }) => <Link {...rest} />)`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;

  ${media.md(css`
    &:hover {
      & + ${ImageWrap} > ${Image} {
        transform: scale(1.035);
        opacity: 1;
      }
    }
  `)}
`

const InnerLink = styled(({ ...rest }) => <Link {...rest} />)`
  position: relative;
  z-index: 2;
  display: block;

  &:hover {
    color: ${colors.softBlue};
  }
`

const DateWrap = styled.div`
  display: flex;
  opacity: 0.7;
  &::before {
    content: '—';
    display: block;
    margin-right: 10px;
    font-size: 12px;
    ${media.md(css`
      font-size: 16px;
    `)}
  }
`

const GradientDecorator = styled(ResponsiveParallax)`
  position: absolute;
  background: linear-gradient(300deg, #d8e7e6 2.43%, #f5f6f5 97.27%);
  z-index: -1;
  top: 40px;
  bottom: 40px;
  left: 40%;
  right: 16px;

  ${media.md(css`
    top: 0;
    bottom: 40px;
    left: 100px;
    right: 0;
  `)}
`

export const LatestStory = ({ title, date, topic, image, link }) => {
  const topicSlug = get(topic, 'fields.topicSlug', '')
  const topicName = get(topic, 'fields.topicName', '')
  const { i18n, t } = useTranslation()
  return (
    <Container>
      <OuterLink to={link} />
      <ImageWrap>
        <Image image={image} ratio={1.3} />
      </ImageWrap>
      <Content>
        <Type
          preset="labelSmall"
          weight="bold"
          color={colors.deepBlue}
          top={{ xs: 2 }}
          bottom={{ xs: 1 }}
        >
          <InnerLink to={t('storyTopicPath', { topicSlug })}>
            {topicName}
          </InnerLink>
        </Type>
        <Type preset="headlineLarge" weight="bold" bottom={{ xs: 1 }}>
          {title}
        </Type>
        <DateWrap>
          <Type size={{ xs: 12, md: 16 }} bottom={{ xs: 4, md: 3 }}>
            {formatDateInUTC(date, 'PP', i18n.language)}
          </Type>
        </DateWrap>
      </Content>
      <GradientDecorator from={8} to={-2} disabledMobile />
    </Container>
  )
}

LatestStory.defaultProps = {
  link: '/stories',
}
